import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { X, DollarSign, Clock, Repeat, Plus, Minus } from 'lucide-react';
import { Task } from '../types';
interface TaskCardProps {
  task: Task;
  onUpdate: (id: string, updates: Partial<Task>) => void;
  onDelete?: (id: string) => void;
}
interface PillRowProps {
  value: number | null;
  onChange: (val: number) => void;
  ariaLabel: string;
}
function PillRow({
  value,
  onChange,
  ariaLabel
}: PillRowProps) {
  return <div className="flex gap-2" role="group" aria-label={ariaLabel} data-id="element-221">
      {[1, 2, 3, 4, 5].map(num => {
      const isSelected = value === num;
      return <motion.button key={num} type="button" whileHover={{
        scale: 1.08,
        y: -2
      }} whileTap={{
        scale: 0.92
      }} onClick={() => onChange(num)} aria-pressed={isSelected} className={`w-11 h-11 rounded-2xl font-bold text-base border-2 transition-colors flex items-center justify-center ${isSelected ? 'bg-ink text-white border-ink shadow-soft-sm' : 'bg-white border-ink/15 text-ink/50 hover:border-ink/40 hover:text-ink'}`} data-id="element-222">
            {num}
          </motion.button>;
    })}
    </div>;
}
interface HoursStepperProps {
  value: number;
  onChange: (val: number) => void;
}
function HoursStepper({
  value,
  onChange
}: HoursStepperProps) {
  const increment = () => onChange(value + 1);
  const decrement = () => onChange(Math.max(0, value - 1));
  return <div className="flex items-center gap-3" data-id="element-223">
      <div className="flex items-center bg-white border-2 border-ink/15 rounded-2xl overflow-hidden" data-id="element-224">
        <button type="button" onClick={decrement} className="w-10 h-11 flex items-center justify-center text-ink/50 hover:bg-ink/5 hover:text-ink transition-colors" aria-label="Decrease hours" data-id="element-225">
          <Minus size={16} strokeWidth={3} data-id="element-226" />
        </button>
        <div className="w-14 h-11 flex items-center justify-center font-bold text-lg text-ink border-x-2 border-ink/10" data-id="element-227">
          {value}
        </div>
        <button type="button" onClick={increment} className="w-10 h-11 flex items-center justify-center text-ink/50 hover:bg-ink/5 hover:text-ink transition-colors" aria-label="Increase hours" data-id="element-228">
          <Plus size={16} strokeWidth={3} data-id="element-229" />
        </button>
      </div>
      <span className="text-ink/60 font-semibold text-sm" data-id="element-230">hours / week</span>
    </div>;
}
export function TaskCard({
  task,
  onUpdate,
  onDelete
}: TaskCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);
  return <motion.div layout initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    scale: 0.9
  }} className="bg-white rounded-3xl shadow-soft border-2 border-ink/5 overflow-hidden" data-id="element-231">
      {/* Header */}
      <div className="flex items-stretch gap-3 p-4 bg-butter/40 border-b-2 border-ink/5" data-id="element-232">
        <div className="flex-1 bg-butter/60 rounded-2xl px-5 py-4 flex items-center" data-id="element-233">
          {isEditing ? <input ref={inputRef} type="text" value={task.name} onChange={e => onUpdate(task.id, {
          name: e.target.value
        })} onBlur={() => setIsEditing(false)} onKeyDown={e => e.key === 'Enter' && setIsEditing(false)} className="w-full text-lg md:text-xl font-extrabold text-ink bg-transparent outline-none" data-id="element-234" /> : <button type="button" onClick={() => setIsEditing(true)} className="text-lg md:text-xl font-extrabold text-ink text-left w-full hover:text-grape transition-colors" data-id="element-235">
              {task.name}
            </button>}
        </div>
        {task.isCustom && onDelete && <button onClick={() => onDelete(task.id)} className="w-12 flex items-center justify-center text-ink/40 hover:text-coral bg-white rounded-2xl border-2 border-ink/10 hover:border-coral/30 transition-colors" aria-label="Delete task" data-id="element-236">
            <X size={20} strokeWidth={3} data-id="element-237" />
          </button>}
      </div>

      {/* Scales */}
      <div className="divide-y-2 divide-ink/5" data-id="element-238">
        {/* Revenue Impact */}
        <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6 p-5 md:p-6" data-id="element-239">
          <div className="md:w-64 flex-shrink-0" data-id="element-240">
            <div className="flex items-center gap-2 mb-1" data-id="element-241">
              <DollarSign size={18} className="text-coral" strokeWidth={2.5} data-id="element-242" />
              <h4 className="text-base font-extrabold text-ink" data-id="element-243">
                Revenue impact
              </h4>
            </div>
            <p className="text-sm text-ink/50 font-medium leading-snug" data-id="element-244">
              1 = admin / no revenue · 5 = direct revenue driver
            </p>
          </div>
          <PillRow value={task.revenueImpact} onChange={val => onUpdate(task.id, {
          revenueImpact: val
        })} ariaLabel="Revenue impact rating" data-id="element-245" />
        </div>

        {/* Time Spent */}
        <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6 p-5 md:p-6" data-id="element-246">
          <div className="md:w-64 flex-shrink-0" data-id="element-247">
            <div className="flex items-center gap-2 mb-1" data-id="element-248">
              <Clock size={18} className="text-grape" strokeWidth={2.5} data-id="element-249" />
              <h4 className="text-base font-extrabold text-ink" data-id="element-250">Time spent</h4>
            </div>
            <p className="text-sm text-ink/50 font-medium leading-snug" data-id="element-251">
              Actual hours per week
            </p>
          </div>
          <HoursStepper value={task.timeSpent} onChange={val => onUpdate(task.id, {
          timeSpent: val
        })} data-id="element-252" />
        </div>

        {/* Repeatable */}
        <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6 p-5 md:p-6" data-id="element-253">
          <div className="md:w-64 flex-shrink-0" data-id="element-254">
            <div className="flex items-center gap-2 mb-1" data-id="element-255">
              <Repeat size={18} className="text-leaf" strokeWidth={2.5} data-id="element-256" />
              <h4 className="text-base font-extrabold text-ink" data-id="element-257">
                How repeatable
              </h4>
            </div>
            <p className="text-sm text-ink/50 font-medium leading-snug" data-id="element-258">
              1 = different every time · 5 = same steps every time
            </p>
          </div>
          <PillRow value={task.repeatability} onChange={val => onUpdate(task.id, {
          repeatability: val
        })} ariaLabel="Repeatability rating" data-id="element-259" />
        </div>
      </div>
    </motion.div>;
}